/**
 * A quote started in the office (QUOTE-12): who and where, a title, and
 * optionally the first kit. Everything else is added on the quote itself,
 * which opens straight after.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { CustomerSitePicker } from "../components/CustomerSitePicker";
import { Card, PageHeader } from "../components/ui";

export default function NewQuote() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState(params.get("customer") ?? "");
  const [siteId, setSiteId] = useState(params.get("site") ?? "");
  const [title, setTitle] = useState("");
  const [kitId, setKitId] = useState("");
  const [driver, setDriver] = useState("1");
  const [problem, setProblem] = useState<string | null>(null);
  const kits = useQuery({ queryKey: ["kits"], queryFn: () => api.catalogue.kits() });
  const kit = kits.data?.find((k) => k.id === kitId);

  const create = useMutation({
    mutationFn: () =>
      api.quotes.create({
        customer_id: customerId,
        site_id: siteId,
        title: title.trim() || kit?.name || "Quote",
        sections: kitId ? [{ kit_id: kitId, driver_qty: Number(driver) || 1 }] : [],
      }),
    onSuccess: (quote) => {
      void queryClient.invalidateQueries({ queryKey: ["quotes"] });
      navigate(`/quotes/${quote.id}`);
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't start the quote."),
  });

  return (
    <div className="page">
      <PageHeader title="New quote">Who it's for and where. Kits and lines go on next.</PageHeader>
      <Card title="Customer and site">
        <CustomerSitePicker customerId={customerId} siteId={siteId} onCustomer={setCustomerId} onSite={setSiteId} />
      </Card>
      <Card title="The quote">
        <div className="form-grid">
          <label className="field">
            <span>Title — what the customer sees at the top</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kit?.name ?? "CCTV upgrade — car park"} />
          </label>
          <label className="field">
            <span>Start from a kit (optional)</span>
            <select
              value={kitId}
              onChange={(e) => {
                setKitId(e.target.value);
                const k = kits.data?.find((x) => x.id === e.target.value);
                if (k) setDriver(String(k.default_driver_qty));
              }}
            >
              <option value="">Nothing yet</option>
              {(kits.data ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </label>
          {kit && (
            <label className="field">
              <span>How many {kit.driver_label}</span>
              <input value={driver} onChange={(e) => setDriver(e.target.value)} inputMode="decimal" />
            </label>
          )}
        </div>
      </Card>
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      <div className="btn-row">
        <button
          className="btn btn-primary"
          type="button"
          disabled={!customerId || !siteId || create.isPending || (!title.trim() && !kit)}
          onClick={() => {
            setProblem(null);
            create.mutate();
          }}
        >
          {create.isPending ? "Starting…" : "Start the quote"}
        </button>
        <button className="btn" type="button" onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
